import axiosConfig from "./axiosConfig";

export type GprTerm =
  | { type: "flat"; valuePct: number }
  | { type: "fractionOfDesignedPr"; fraction: number }
  | { type: "designedPrMinus"; deltaPct: number };

export type GprRule =
  | GprTerm
  | { type: "max"; terms: [GprTerm, GprTerm] }
  | { type: "min"; terms: [GprTerm, GprTerm] };

export interface SiteHealthConfig {
  siteId: string;
  alertingEnabled: boolean;
  unreliableConnection: boolean;
  lateDataThresholdSeconds: number;
  /** @deprecated replaced by pvAvailability + bessAvailability; retained during transition */
  contractedAvailability: number | null;
  /** PV / BESS availability guarantee targets (%), per the amended EPC (PV 98,
   * BESS 97). pvAvailability is the PV availability RYG threshold. bessAvailability
   * is stored for config; the BESS availability metric that grades against it is
   * built separately. */
  pvAvailability: number | null;
  bessAvailability: number | null;
  /** @deprecated replaced by designedPrPct + gprRule; retained during transition */
  contractedGpr: number | null;
  designedPrPct: number | null;
  gprRule: GprRule | null;
  weatherActualsEnabled: boolean;
  /** Reduxi-only grid-import limits (kW), both optional. See the import-limit
   * health check. maxImportEnforcedKw must be <= maxImportAllowedKw when both set. */
  maxImportAllowedKw: number | null;
  maxImportEnforcedKw: number | null;
  /** Per-inverter temperature monitoring (SolarEdge sites). When enabled, the
   * hourly check Slack-alerts if any inverter runs hot. Both thresholds
   * optional; fall back to 70C warning / 75C alert when null. */
  inverterTempMonitoringEnabled: boolean;
  inverterTempWarningC: number | null;
  inverterTempAlertC: number | null;
  createdAt: string;
  updatedAt: string;
}

const getSiteHealthConfig = async (
  authToken: string,
  siteId: string
): Promise<SiteHealthConfig | null> => {
  const { data } = await axiosConfig.get(`/site/${siteId}/health/config`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  // BMA returns `{healthConfig: null}` (200) when the site has no health
  // config — a legitimate state, not an error. Normalise to null for callers.
  if (data == null || data.healthConfig === null) {
    return null;
  }
  return data;
};

export default getSiteHealthConfig;
